import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { DeepChat } from "deep-chat-react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { ChannelActions, useAppDispatch, useAppSelector } from "@Store";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { DialogComponents } from "@Interfaces/Entities/Dialogs";
import {
    saveAssistantChart,
    type StoredAssistantChart,
} from "@Utils/assistantChartsStorage";
import { hasAiAssistantAccess } from "@Utils/accountEntitlements";
import { getAssistantContextCandidates } from "../../../Utils/assistantContextChannels";
import { downloadConversationPdf } from "@Components/AssistantConversationPdf/Utils/downloadConversationPdf";
import {
    assistantComparisonCurrentBadgeSx,
    assistantComparisonEventIconSx,
    assistantComparisonEventRowSx,
    assistantComparisonEventTitleSx,
    assistantComparisonListSx,
    assistantComparisonPopoverPaperSx,
    assistantComparisonPopoverContentSx,
    assistantComparisonPopoverTitleSx,
    assistantComparisonRemoveButtonSx,
    assistantComparisonSearchFieldSx,
    assistantComparisonSearchResultButtonSx,
    assistantComparisonSearchResultsSx,
    assistantComparisonSearchStateSx,
    assistantComparisonTriggerSx,
    assistantTabChatBodySx,
    assistantTabHeaderSx,
    assistantTabLoadingBoxSx,
    assistantTabPaperSx,
    createDeepChatNames,
    createDeepChatTextInput,
    deepChatAuxiliaryStyle,
    deepChatAvatars,
    deepChatExportIconSrc,
    deepChatMessageStyles,
    deepChatStyle,
    deepChatSubmitButtonStyles,
} from "./AssistantTab.styled";

const buildIntroText = (
    translate: (key: string) => string,
    executiveSummary: string | null
): string => {
    const greeting = translate("pages.eventInsights.assistant.greeting");
    const closer = translate("pages.eventInsights.assistant.closer");
    const bridge = translate("pages.eventInsights.assistant.summaryBridge");

    if (!executiveSummary) {
        return `${greeting}\n\n${closer}`;
    }

    return `${greeting}\n\n${bridge}\n\n${executiveSummary}\n\n${closer}`;
};

const CHAT_ERROR_TRANSLATION_KEYS: Record<string, string> = {
    ASSISTANT_CHAT_NO_BUNDLE: "pages.eventInsights.chatErrors.noBundle",
    ASSISTANT_CHAT_UNCOMPLETE_REQUEST:
        "pages.eventInsights.chatErrors.uncompleteRequest",
};

interface AssistantTabProps {
    channelId: string;
    eventTitle: string;
}

interface DeepChatRequestBody {
    messages?: Array<{ role?: string; text?: string; content?: string }>;
}

interface DeepChatSignals {
    onResponse: (response: any) => Promise<void>;
}

const formatChatTimestamp = () =>
    new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        minute: "2-digit",
    }).format(new Date());

const ensureDateDivider = (
    messagesContainer: Element,
    dividerLabel: string
) => {
    if (messagesContainer.querySelector(".tendbe-chat-date-divider")) return;

    const divider = document.createElement("div");
    divider.className = "tendbe-chat-date-divider";
    divider.textContent = dividerLabel;
    messagesContainer.prepend(divider);
};

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

const buildChartsHtml = (
    charts: StoredAssistantChart[],
    buildButtonText: (title: string) => string
) => {
    const buttons = charts
        .map((stored) => {
            const buttonText = escapeHtml(
                buildButtonText(stored.chart.title || "")
            );

            return `
<button class="deep-chat-button assistant-chart-button" data-chart-id="${stored.chartId}" style="margin-top: 6px; width: 100%;">
  ${buttonText}
</button>`;
        })
        .join("");

    return `<div>${buttons}</div>`;
};

export const AssistantTab = ({ channelId, eventTitle }: AssistantTabProps) => {
    const deepChatRef = useRef<any>(null);
    const channelIdRef = useRef(channelId);
    const selectedAssistantContextIdsRef = useRef<string[]>([]);
    const [introText, setIntroText] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [comparisonPopoverAnchorEl, setComparisonPopoverAnchorEl] =
        useState<HTMLElement | null>(null);
    const [comparisonSearchValue, setComparisonSearchValue] = useState("");
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const accountType = useAppSelector(
        (state) => state.auth.user.account?.type
    );
    const channels = useAppSelector((state) => state.channels.channels);
    const assistantContextAvailabilityById = useAppSelector(
        (state) => state.channels.assistantContextAvailabilityById
    );
    const currentChannel = useAppSelector(
        (state) =>
            state.channels.channels.find(
                (channel) => channel._id === channelId
            ) ?? null
    );
    const selectedAssistantContextIds = useAppSelector(
        (state) => state.channels.selectedAssistantContexts[channelId] ?? []
    );
    const resolvedEventTitle = eventTitle || currentChannel?.title || "";

    const hasPremiumAiAccess = hasAiAssistantAccess(accountType);
    const deepChatTextInput = createDeepChatTextInput(
        t("pages.eventInsights.assistant.inputPlaceholder")
    );
    const deepChatNames = useMemo(
        () => createDeepChatNames(t("pages.eventInsights.assistant.aiName")),
        [t]
    );
    const assistantContextCandidates = useMemo(
        () => getAssistantContextCandidates(currentChannel, channels),
        [channels, currentChannel]
    );
    const assistantContextTitlesById = useMemo(
        () =>
            assistantContextCandidates.reduce<Record<string, string>>(
                (accumulator, candidate) => {
                    accumulator[candidate._id] = candidate.title;
                    return accumulator;
                },
                {}
            ),
        [assistantContextCandidates]
    );
    const allowedAssistantContextIds = useMemo(
        () =>
            new Set(
                assistantContextCandidates.map((candidate) => candidate._id)
            ),
        [assistantContextCandidates]
    );
    const selectedComparisonEvents = useMemo(
        () =>
            selectedAssistantContextIds.map((contextId) => {
                const hydratedChannel = channels.find(
                    (channel) => channel._id === contextId
                );

                return {
                    _id: contextId,
                    title:
                        hydratedChannel?.title ??
                        assistantContextTitlesById[contextId] ??
                        contextId,
                };
            }),
        [assistantContextTitlesById, channels, selectedAssistantContextIds]
    );
    const comparisonEventsCount = 1 + selectedAssistantContextIds.length;
    const isComparisonPopoverOpen = Boolean(comparisonPopoverAnchorEl);
    const normalizedComparisonSearchValue = comparisonSearchValue
        .trim()
        .toLocaleLowerCase();
    const comparisonSearchOptions = useMemo(
        () =>
            assistantContextCandidates
                .map((candidate) => {
                    const availability =
                        assistantContextAvailabilityById[candidate._id];
                    const hydratedChannel = channels.find(
                        (channel) => channel._id === candidate._id
                    );
                    const availabilityStatus = availability?.status ?? "idle";

                    return {
                        _id: candidate._id,
                        title:
                            availability?.title ??
                            hydratedChannel?.title ??
                            candidate.title,
                        hasReport: availability?.hasAnalysisBundle ?? false,
                        isLoading:
                            availabilityStatus === "loading" ||
                            availabilityStatus === "idle",
                    };
                })
                .filter(
                    (candidate) =>
                        !selectedAssistantContextIds.includes(candidate._id)
                ),
        [
            assistantContextAvailabilityById,
            assistantContextCandidates,
            channels,
            selectedAssistantContextIds,
        ]
    );
    const comparisonSearchResults = useMemo(
        () =>
            normalizedComparisonSearchValue
                ? comparisonSearchOptions.filter(
                      (candidate) =>
                          candidate.hasReport &&
                          !candidate.isLoading &&
                          candidate.title
                              .toLocaleLowerCase()
                              .includes(normalizedComparisonSearchValue)
                  )
                : [],
        [comparisonSearchOptions, normalizedComparisonSearchValue]
    );
    const isComparisonSearchLoading =
        normalizedComparisonSearchValue.length > 0 &&
        comparisonSearchOptions.some((candidate) => candidate.isLoading);
    const hasAnyEligibleComparisonOption = comparisonSearchOptions.some(
        (candidate) => candidate.hasReport || candidate.isLoading
    );

    useEffect(() => {
        channelIdRef.current = channelId;
    }, [channelId]);

    useEffect(() => {
        setComparisonPopoverAnchorEl(null);
        setComparisonSearchValue("");
    }, [channelId]);

    useEffect(() => {
        selectedAssistantContextIdsRef.current = selectedAssistantContextIds;
    }, [selectedAssistantContextIds]);

    useEffect(() => {
        if (!channelId) return;

        dispatch(ChannelActions.loadAssistantContexts(channelId));
    }, [channelId, dispatch]);

    useEffect(() => {
        if (assistantContextCandidates.length === 0) {
            return;
        }

        dispatch(
            ChannelActions.prefetchAssistantContextAvailability(
                assistantContextCandidates.map((candidate) => candidate._id)
            )
        );
    }, [assistantContextCandidates, dispatch]);

    useEffect(() => {
        if (!channelId) return;

        const filteredContextIds = selectedAssistantContextIds.filter(
            (contextId) => allowedAssistantContextIds.has(contextId)
        );

        if (filteredContextIds.length === selectedAssistantContextIds.length) {
            return;
        }

        dispatch(
            ChannelActions.saveAssistantContexts({
                channelId,
                contextIds: filteredContextIds,
            })
        );
    }, [
        allowedAssistantContextIds,
        channelId,
        dispatch,
        selectedAssistantContextIds,
    ]);

    const decorateMessageTimestamps = useCallback(() => {
        window.setTimeout(() => {
            const shadowRoot = deepChatRef.current?.shadowRoot as
                | ShadowRoot
                | null
                | undefined;

            if (!shadowRoot) return;

            const messageElements = Array.from(
                shadowRoot.querySelectorAll(
                    '[class*="deep-chat-outer-container-role-"]'
                )
            ) as HTMLElement[];
            const messagesContainer = messageElements[0]?.parentElement;
            if (messagesContainer) {
                ensureDateDivider(
                    messagesContainer,
                    t("pages.eventInsights.assistant.todayDivider")
                );
            }

            messageElements.forEach((messageElement) => {
                if (messageElement.dataset.tendbeTimestamp === "true") return;

                const isUserMessage = messageElement.classList.contains(
                    "deep-chat-outer-container-role-user"
                );
                const timestamp = document.createElement("div");
                timestamp.className = `tendbe-chat-timestamp ${
                    isUserMessage
                        ? "tendbe-chat-timestamp-user"
                        : "tendbe-chat-timestamp-ai"
                }`;
                timestamp.textContent = formatChatTimestamp();

                if (isUserMessage) {
                    const checkmark = document.createElement("span");
                    checkmark.className = "tendbe-chat-checkmark";
                    checkmark.textContent = "✓";
                    timestamp.appendChild(checkmark);
                }

                messageElement.dataset.tendbeTimestamp = "true";
                messageElement.insertAdjacentElement("afterend", timestamp);
            });
        }, 0);
    }, [t]);

    useEffect(() => {
        if (!channelId) return;

        setIntroText(null);
        dispatch(ChannelActions.fetchChannelSummary(channelId))
            .then((executiveSummary: string | null) => {
                setIntroText(buildIntroText(t, executiveSummary));
            })
            .catch(() => {
                setIntroText(buildIntroText(t, null));
            });
    }, [channelId, dispatch, t]);

    useEffect(() => {
        if (introText === null) return;

        const timeouts = [50, 300, 900].map((delay) =>
            window.setTimeout(decorateMessageTimestamps, delay)
        );

        return () => {
            timeouts.forEach((timeout) => window.clearTimeout(timeout));
        };
    }, [decorateMessageTimestamps, introText]);

    const htmlClassUtilities = useMemo(
        () => ({
            "assistant-chart-button": {
                events: {
                    click: (event: any) => {
                        const target = event?.target as HTMLElement | null;
                        const el =
                            (target?.closest?.(
                                "[data-chart-id]"
                            ) as HTMLElement | null) ?? target;
                        const chartId =
                            el?.getAttribute?.("data-chart-id") ?? null;

                        if (!chartId) return;

                        dispatch(
                            dialogActions.openDialog({
                                id: DialogComponents.AssistantChartPreviewDialog,
                                props: {
                                    channelId,
                                    chartId,
                                    maxWidth: "lg",
                                },
                            })
                        );
                    },
                },
                styles: {
                    default: { cursor: "pointer" },
                },
            },
        }),
        [channelId, dispatch]
    );

    const connectHandler = useCallback(
        async (body: DeepChatRequestBody, signals: DeepChatSignals) => {
            const prompt = [...(body.messages ?? [])]
                .reverse()
                .find((message) => message.role === "user");
            const userPrompt = (prompt?.text ?? prompt?.content ?? "").trim();

            if (!userPrompt) {
                await signals.onResponse({
                    text: t("pages.eventInsights.chatErrors.uncompleteRequest"),
                });
                return;
            }

            try {
                const activeChannelId = channelIdRef.current;
                const reply = await dispatch(
                    ChannelActions.sendAssistantChatMessage({
                        channelId: activeChannelId,
                        contextChannelIds:
                            selectedAssistantContextIdsRef.current,
                        prompt: userPrompt,
                    })
                );

                if (reply.errorMessage) {
                    const translationKey =
                        CHAT_ERROR_TRANSLATION_KEYS[reply.errorMessage];

                    await signals.onResponse({
                        text: translationKey
                            ? t(translationKey)
                            : t("pages.eventInsights.chatErrors.generic"),
                    });
                    return;
                }

                const charts = reply.charts ?? [];
                const responses: any[] = [{ text: reply.text ?? "" }];

                if (charts.length > 0) {
                    const storedCharts = charts.map((chart) =>
                        saveAssistantChart(activeChannelId, chart)
                    );

                    responses.push({
                        html: buildChartsHtml(storedCharts, (title) => {
                            const trimmed = title.trim();

                            if (!trimmed) {
                                return t(
                                    "pages.eventInsights.assistant.charts.openGeneratedChartButton"
                                );
                            }

                            return t(
                                "pages.eventInsights.assistant.charts.openChartButton",
                                { title: trimmed }
                            );
                        }),
                    });
                }

                await signals.onResponse(
                    responses.length === 1 ? responses[0] : responses
                );
            } catch {
                await signals.onResponse({
                    text: t("pages.eventInsights.chatErrors.generic"),
                });
            }
        },
        [dispatch, t]
    );

    const handleExportConversation = useCallback(async () => {
        if (!channelId || isExporting || !hasPremiumAiAccess) return;

        setIsExporting(true);

        try {
            await downloadConversationPdf({
                channelId,
                eventTitle: resolvedEventTitle,
                documentTitle: t(
                    "pages.eventInsights.assistant.conversationDocumentTitle"
                ),
                emptyStateText: t(
                    "pages.eventInsights.assistant.conversationEmptyState"
                ),
            });
        } catch {
            dispatch(
                dialogActions.openDialog({
                    id: DialogComponents.AssistantConversationExportErrorDialog,
                    props: {
                        maxWidth: "sm",
                    },
                })
            );
        } finally {
            setIsExporting(false);
        }
    }, [
        channelId,
        dispatch,
        resolvedEventTitle,
        hasPremiumAiAccess,
        isExporting,
        t,
    ]);

    const handleRemoveComparisonEvent = useCallback(
        (contextId: string) => {
            dispatch(
                ChannelActions.saveAssistantContexts({
                    channelId,
                    contextIds: selectedAssistantContextIds.filter(
                        (selectedId) => selectedId !== contextId
                    ),
                })
            );
        },
        [channelId, dispatch, selectedAssistantContextIds]
    );

    const handleAddComparisonEvent = useCallback(
        (contextId: string) => {
            if (
                !channelId ||
                selectedAssistantContextIds.includes(contextId) ||
                !allowedAssistantContextIds.has(contextId)
            ) {
                return;
            }

            dispatch(
                ChannelActions.saveAssistantContexts({
                    channelId,
                    contextIds: [...selectedAssistantContextIds, contextId],
                })
            );
            setComparisonSearchValue("");
        },
        [
            allowedAssistantContextIds,
            channelId,
            dispatch,
            selectedAssistantContextIds,
        ]
    );

    const handleToggleComparisonPopover = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            setComparisonPopoverAnchorEl((previousAnchor) =>
                previousAnchor === event.currentTarget
                    ? null
                    : event.currentTarget
            );
        },
        []
    );

    useEffect(() => {
        const mountExportButton = () => {
            const shadowRoot = deepChatRef.current?.shadowRoot as
                | ShadowRoot
                | null
                | undefined;
            if (!shadowRoot) return;

            const inputContainer = shadowRoot.querySelector(
                "#text-input-container"
            ) as HTMLElement | null;
            if (!inputContainer) return;

            let exportButton = inputContainer.querySelector(
                ".tendbe-chat-export-button"
            ) as HTMLButtonElement | null;

            if (!hasPremiumAiAccess) {
                if (exportButton) {
                    exportButton.remove();
                }
                return;
            }

            if (!exportButton) {
                exportButton = document.createElement("button");
                exportButton.type = "button";
                exportButton.className = "tendbe-chat-export-button";

                const icon = document.createElement("img");
                icon.src = deepChatExportIconSrc;
                icon.alt = "";
                exportButton.appendChild(icon);

                inputContainer.appendChild(exportButton);
            }

            exportButton.title = t(
                "pages.eventInsights.assistant.downloadConversation"
            );
            exportButton.setAttribute(
                "aria-label",
                t("pages.eventInsights.assistant.downloadConversation")
            );
            exportButton.disabled = isExporting || !channelId;
            exportButton.onclick = () => {
                void handleExportConversation();
            };
        };

        const timeouts = [0, 120, 360].map((delay) =>
            window.setTimeout(mountExportButton, delay)
        );

        return () => {
            timeouts.forEach((timeout) => window.clearTimeout(timeout));
        };
    }, [
        channelId,
        handleExportConversation,
        hasPremiumAiAccess,
        introText,
        isExporting,
        t,
    ]);

    let comparisonSearchContent: React.ReactNode;

    if (normalizedComparisonSearchValue.length === 0) {
        comparisonSearchContent = (
            <Typography sx={assistantComparisonSearchStateSx}>
                {t(
                    hasAnyEligibleComparisonOption
                        ? "pages.eventInsights.assistant.context.searchHint"
                        : "pages.eventInsights.assistant.context.emptyState"
                )}
            </Typography>
        );
    } else if (
        isComparisonSearchLoading &&
        comparisonSearchResults.length === 0
    ) {
        comparisonSearchContent = (
            <Typography sx={assistantComparisonSearchStateSx}>
                {t("pages.eventInsights.assistant.context.loadingOption")}
            </Typography>
        );
    } else if (comparisonSearchResults.length > 0) {
        comparisonSearchContent = (
            <>
                {comparisonSearchResults.map((comparisonEvent) => (
                    <Button
                        key={comparisonEvent._id}
                        type="button"
                        variant="text"
                        startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
                        onClick={() =>
                            handleAddComparisonEvent(comparisonEvent._id)
                        }
                        sx={assistantComparisonSearchResultButtonSx}
                    >
                        {comparisonEvent.title}
                    </Button>
                ))}
            </>
        );
    } else {
        comparisonSearchContent = (
            <Typography sx={assistantComparisonSearchStateSx}>
                {t("pages.eventInsights.assistant.context.noSearchResults")}
            </Typography>
        );
    }

    return (
        <Paper
            elevation={0}
            sx={assistantTabPaperSx}
            data-assistant-export-root="true"
        >
            {hasPremiumAiAccess ? (
                <Box sx={assistantTabHeaderSx}>
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={handleToggleComparisonPopover}
                        sx={assistantComparisonTriggerSx}
                    >
                        {selectedAssistantContextIds.length > 0
                            ? `${t(
                                  "pages.eventInsights.assistant.context.buttonWithCount",
                                  {
                                      count: comparisonEventsCount,
                                  }
                              )} ▼`
                            : `${t(
                                  "pages.eventInsights.assistant.context.button"
                              )} ▼`}
                    </Button>
                </Box>
            ) : null}
            {introText === null ? (
                <Box sx={assistantTabLoadingBoxSx}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary">
                        {t("pages.eventInsights.assistant.loading")}
                    </Typography>
                </Box>
            ) : (
                <Box sx={assistantTabChatBodySx}>
                    <DeepChat
                        ref={deepChatRef}
                        connect={{
                            handler: connectHandler,
                        }}
                        introMessage={{ text: introText }}
                        browserStorage={{
                            key: `insights-chat-${channelId}`,
                            maxMessages: 40,
                            inputText: false,
                        }}
                        onMessage={decorateMessageTimestamps as any}
                        textInput={deepChatTextInput}
                        avatars={deepChatAvatars}
                        names={deepChatNames}
                        messageStyles={deepChatMessageStyles}
                        submitButtonStyles={deepChatSubmitButtonStyles}
                        auxiliaryStyle={deepChatAuxiliaryStyle}
                        remarkable={{ breaks: true }}
                        displayLoadingBubble
                        htmlClassUtilities={htmlClassUtilities as any}
                        style={deepChatStyle}
                    />
                </Box>
            )}
            <Popover
                open={isComparisonPopoverOpen}
                anchorEl={comparisonPopoverAnchorEl}
                onClose={() => {
                    setComparisonPopoverAnchorEl(null);
                    setComparisonSearchValue("");
                }}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                PaperProps={{
                    sx: assistantComparisonPopoverPaperSx,
                }}
            >
                <Box sx={assistantComparisonPopoverContentSx}>
                    <Typography sx={assistantComparisonPopoverTitleSx}>
                        {t("pages.eventInsights.assistant.context.title")}
                    </Typography>

                    <Box sx={assistantComparisonListSx}>
                        <Box sx={assistantComparisonEventRowSx}>
                            <CheckRoundedIcon
                                sx={assistantComparisonEventIconSx}
                            />
                            <Typography
                                variant="body2"
                                sx={assistantComparisonEventTitleSx}
                            >
                                {resolvedEventTitle}
                            </Typography>
                            <Box
                                component="span"
                                sx={assistantComparisonCurrentBadgeSx}
                            >
                                {t(
                                    "pages.eventInsights.assistant.context.currentLabel"
                                )}
                            </Box>
                        </Box>

                        {selectedComparisonEvents.map((comparisonEvent) => (
                            <Box
                                key={comparisonEvent._id}
                                sx={assistantComparisonEventRowSx}
                            >
                                <CheckRoundedIcon
                                    sx={assistantComparisonEventIconSx}
                                />
                                <Typography
                                    variant="body2"
                                    sx={assistantComparisonEventTitleSx}
                                >
                                    {comparisonEvent.title}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        handleRemoveComparisonEvent(
                                            comparisonEvent._id
                                        )
                                    }
                                    aria-label={t(
                                        "pages.eventInsights.assistant.context.removeButtonLabel"
                                    )}
                                    sx={assistantComparisonRemoveButtonSx}
                                >
                                    <CloseRoundedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>

                    <Divider />

                    <TextField
                        fullWidth
                        size="small"
                        value={comparisonSearchValue}
                        onChange={(event) =>
                            setComparisonSearchValue(event.target.value)
                        }
                        placeholder={t(
                            "pages.eventInsights.assistant.context.searchPlaceholder"
                        )}
                        aria-label={t(
                            "pages.eventInsights.assistant.context.searchLabel"
                        )}
                        sx={assistantComparisonSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon
                                        sx={{
                                            fontSize: 18,
                                            color: "#94a3b8",
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Box sx={assistantComparisonSearchResultsSx}>
                        {comparisonSearchContent}
                    </Box>
                </Box>
            </Popover>
        </Paper>
    );
};
